import { Banner, Button, Checkbox, SearchBar } from '@ordr-as/umami-react'
import AddLocationDialog from 'components/AddLocationDialog'
import Chip from 'components/Chip'
import ChipGroup from 'components/Chip/ChipGroup'
import ListCard from 'components/ListCard/ListCard'
import ListCardGroup from 'components/ListCard/ListCardGroup'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { mapFavritAppToName } from 'utils/favritAppUtils'
import {
    PaymentMethodConnectionActivityStateDto,
    PaymentMethodLocationDto,
    PaymentProvider,
} from '../../../generated-api'

const activityStateSortOrder = [
    PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW,
    PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST,
    PaymentMethodConnectionActivityStateDto.WILL_BE_ACTIVE_IN_THE_FUTURE,
]

type PaymentMethodLocationListProps = {
    locations: PaymentMethodLocationDto[]
    paymentProvider: PaymentProvider
    paymentMethod: string
}

const PaymentMethodLocationList = (props: PaymentMethodLocationListProps) => {
    const { locations, paymentProvider, paymentMethod } = props
    const [filteredLocations, setFilteredLocations] = useState<PaymentMethodLocationDto[]>([])
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [showPastActiveLocations, setShowPastActiveLocations] = useState(false)
    const [showFutureActiveLocations, setShowFutureActiveLocations] = useState(false)
    const [addLocationDialogIsOpen, setAddLocationDialogIsOpen] = useState(false)
    const hasPastActiveLocations = useMemo(
        () => locations.some((l) => l.activityState === PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST),
        [locations]
    )
    const hasFutureActiveLocations = useMemo(
        () =>
            locations.some(
                (l) => l.activityState === PaymentMethodConnectionActivityStateDto.WILL_BE_ACTIVE_IN_THE_FUTURE
            ),
        [locations]
    )

    const cannotAddLocation = useMemo(() => {
        return ['CASH_WITHOUT_CASHDRAWER'].includes(paymentMethod)
    }, [paymentMethod])

    const navigate = useNavigate()

    const navLocation = useLocation()
    const selectedLocationId = navLocation.pathname.split('/')[5]

    useEffect(() => {
        if (!locations) {
            return
        }

        const trimmedSearchQuery = searchQuery.trim()
        const searchQueryLowerCase = trimmedSearchQuery.toLowerCase()

        const filtered = locations.filter((l) => {
            if (
                !showPastActiveLocations &&
                l.activityState === PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST
            ) {
                return false
            }

            if (
                !showFutureActiveLocations &&
                l.activityState === PaymentMethodConnectionActivityStateDto.WILL_BE_ACTIVE_IN_THE_FUTURE
            ) {
                return false
            }

            return (
                l.locationId.toString().includes(searchQueryLowerCase) ||
                l.locationName.toLowerCase().includes(searchQueryLowerCase)
            )
        })

        const filteredAndSorted = filtered.sort((a, b) => {
            return activityStateSortOrder.indexOf(a.activityState) - activityStateSortOrder.indexOf(b.activityState)
        })

        setFilteredLocations(filteredAndSorted)
    }, [locations, showPastActiveLocations, showFutureActiveLocations, searchQuery])

    return (
        <div>
            <div className="mb-4 flex gap-2">
                <SearchBar
                    value={searchQuery}
                    placeholderText="Search location ID or name"
                    aria-label="Search location ID or name"
                    setValue={(text) => setSearchQuery(text)}
                    setActive={() => null}
                    fullWidth
                />
                {!cannotAddLocation && (
                    <Button kind="primary" onClick={() => setAddLocationDialogIsOpen(true)}>
                        Add Location
                    </Button>
                )}
            </div>

            {locations.length === 0 && (
                <Banner kind="warn" stretch="parent" onClose={() => null} className="mb-4">
                    There are no locations
                </Banner>
            )}
            <div className="flex">
                {hasPastActiveLocations && (
                    <Checkbox
                        className="mt-2 mb-3 mr-2 text-small text-grey-dark"
                        isSelected={showPastActiveLocations}
                        onChange={setShowPastActiveLocations}
                    >
                        Show past locations
                    </Checkbox>
                )}

                {hasFutureActiveLocations && (
                    <Checkbox
                        className="mt-2 mb-3 mr-2 text-small text-grey-dark"
                        isSelected={showFutureActiveLocations}
                        onChange={setShowFutureActiveLocations}
                    >
                        Show future locations
                    </Checkbox>
                )}
            </div>
            <div>
                {filteredLocations.length === 0 && searchQuery.trim().length > 0 && (
                    <Banner kind="warn" stretch="parent" onClose={() => null}>
                        No locations match the search: `&quot;`{searchQuery}`&quot;`
                    </Banner>
                )}

                <ListCardGroup>
                    {filteredLocations.map((location, index) => (
                        <ListCard
                            key={`${location.paymentProvider}-${location.paymentMethod}-${location.locationId}-${index}`}
                            onClick={() => navigate(`${location.locationId}`)}
                            className={`flex ${
                                selectedLocationId === location.locationId.toString() && 'bg-purple-lightest'
                            }`}
                        >
                            <div className="flex-1 flex flex-col justify-center">
                                <h4
                                    className={`${
                                        location.activityState !==
                                            PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && 'text-grey-dark'
                                    }`}
                                >
                                    {location.locationName} ({location.locationId})
                                </h4>
                            </div>

                            {location.activityState === PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && (
                                <ChipGroup
                                    heading="Enabled in"
                                    showEmptyPlaceholder={location.enabledInApps.size === 0}
                                >
                                    {Array.from(location.enabledInApps).map((app) => (
                                        <Chip
                                            key={`${location.locationId}-${app}`}
                                            size="small"
                                            text={mapFavritAppToName(app)}
                                        />
                                    ))}
                                </ChipGroup>
                            )}

                            {location.activityState !== PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && (
                                <p className="text-small text-grey-dark">
                                    Location has no active configuration, but
                                    {location.activityState ===
                                    PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST
                                        ? ' did '
                                        : ' does '}
                                    have one in the
                                    {location.activityState ===
                                    PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST
                                        ? ' past'
                                        : ' future'}
                                    .
                                </p>
                            )}
                        </ListCard>
                    ))}
                </ListCardGroup>
            </div>

            <AddLocationDialog
                paymentProvider={paymentProvider}
                paymentMethod={paymentMethod}
                dialogIsOpen={addLocationDialogIsOpen}
                onClose={() => setAddLocationDialogIsOpen(false)}
            />
        </div>
    )
}

export default PaymentMethodLocationList
